import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import useDarkMode from '../../../hooks/useDarkMode';
import { useSelector } from 'react-redux';
import moment from 'moment';
import useThemeColor from "../../../hooks/useThemeColor";
import './MUITimePicker.css';

const MUITimePicker = ({
  label,
  value,
  onChange,
  placeholder = "Select time",
  name = "",
  disabled = false,
  clearable = false,
  helperText = "",
  error = false,
  className = "",
  style = {},
  ampm = false,
  format24 = false,
  minuteStep = 1,
  hoursOnly = false,
  ...props
}) => {
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  
  const isDarkMode = useDarkMode();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);
  
  // Helper function to parse time from value
  const parseTimeFromValue = (timeValue) => {
    if (!timeValue) return null;
    let time;
    if (moment.isMoment(timeValue)) {
      time = timeValue.isValid() ? timeValue : null;
    } else {
      time = moment(timeValue, ["h:mm A", "hh:mm A", "h:mm a", "hh:mm a", "H:mm", "HH:mm"], true);
      if (!time.isValid()) time = null;
    }
    return time && time.isValid() ? time : null;
  };
  
  // Initialize state from value prop
  const parsedTime = parseTimeFromValue(value);
  const [currentHour, setCurrentHour] = useState(() => {
    if (parsedTime) {
      const hour24 = parsedTime.hour();
      return ampm ? (hour24 % 12 || 12) : hour24;
    }
    const now = moment();
    return ampm ? (now.hour() % 12 || 12) : now.hour();
  });
  
  const [currentMinute, setCurrentMinute] = useState(() => {
    return parsedTime ? parsedTime.minute() : moment().minute();
  });
  
  const [isPM, setIsPM] = useState(() => {
    return parsedTime ? parsedTime.hour() >= 12 : moment().hour() >= 12;
  });
  const [isOkHovered, setIsOkHovered] = useState(false);
  const [isCancelHovered, setIsCancelHovered] = useState(false);

  const containerRef = useRef(null);
  const popupRef = useRef(null);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  // Function to get darker shade of theme color
  const getDarkerThemeColor = useCallback(() => {
    const colorMap = {
      'red': '#bb2d3b',
      'pink': '#c71952', 
      'orange': '#dc6d0d',
      'yellow': '#e0a800',
      'lime': '#6db312',
      'green': '#157347',
      'teal': '#1ba87f',
      'cyan': '#0baccc',
      'blue': '#0a58ca',
      'purple': '#59359a',
      'indigo': '#520dc2',
      'dark': '#1a1d20'
    }
    return colorMap[appTheme] || '#1ba87f';
  }, [appTheme]);

  

  // Update selected time when value prop changes
  useEffect(() => {
    if (value) {
      let time;
      if (moment.isMoment(value)) {
        time = value.isValid() ? value : null;
      } else {
        // Try parsing with multiple formats
        time = moment(value, ["h:mm A", "hh:mm A", "h:mm a", "hh:mm a", "H:mm", "HH:mm"], true);
        if (!time.isValid()) {
          time = null;
        }
      }
      
      if (time && time.isValid()) {
        setSelectedTime(time);
        const hour24 = time.hour();
        const minute = time.minute();
        
        // Convert to 12-hour format for display when ampm is enabled
        if (ampm) {
          let hour12 = hour24 % 12;
          if (hour12 === 0) hour12 = 12; // 0 or 12 should be 12
          setCurrentHour(hour12);
          setIsPM(hour24 >= 12);
        } else {
          setCurrentHour(hour24);
          setIsPM(false);
        }
        setCurrentMinute(minute);
      } else {
        setSelectedTime(null);
      }
    } else {
      setSelectedTime(null);
    }
  }, [value]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickInsideContainer = containerRef.current && containerRef.current.contains(event.target);
      const isClickInsidePopup = popupRef.current && popupRef.current.contains(event.target);
      
      if (!isClickInsideContainer && !isClickInsidePopup) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      // Use a small delay to avoid immediate closure
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const toggleTimePicker = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!disabled) {
      if (!isOpen) {
        // When opening, update the picker state from selectedTime
        if (selectedTime && selectedTime.isValid()) {
          const hour24 = selectedTime.hour();
          if (ampm) {
            const hour12 = hour24 % 12 || 12;
            setCurrentHour(hour12);
            setIsPM(hour24 >= 12);
          } else {
            setCurrentHour(hour24);
            setIsPM(false);
          }
          setCurrentMinute(hoursOnly ? 0 : selectedTime.minute());
        }
        
        // Calculate popup position when opening
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();

          // Estimate popup dimensions
          const ESTIMATED_POPUP_HEIGHT = 340; // px, matches popup padding + content
          const ESTIMATED_POPUP_MIN_WIDTH = containerRef.current.offsetWidth || 300;

          let top = rect.bottom + 8;
          let left = rect.left;

          // If the popup would overflow the bottom of the viewport, show it above the input
          if (top + ESTIMATED_POPUP_HEIGHT > window.innerHeight - 16) {
            top = rect.top - ESTIMATED_POPUP_HEIGHT - 8;

            // If even above it would go off‑screen, clamp to top padding
            if (top < 8) {
              top = 8;
            }
          }

          // Prevent horizontal overflow (right side)
          const maxLeft = window.innerWidth - ESTIMATED_POPUP_MIN_WIDTH - 16;
          if (left > maxLeft) {
            left = Math.max(8, maxLeft);
          }

          // Prevent going off the left edge
          if (left < 8) {
            left = 8;
          }

          setPopupPosition({ top, left });
        }
      }
      setIsOpen(prev => !prev);
    }
  };

  const handleTimeSelect = () => {
    let hour = currentHour;
    
    // Convert to 24-hour format if ampm is enabled
    if (ampm) {
      if (isPM && hour !== 12) {
        hour += 12;
      } else if (!isPM && hour === 12) {
        hour = 0;
      }
    }
    
    const newTime = moment().hour(hour).minute(hoursOnly ? 0 : currentMinute).second(0);
    setSelectedTime(newTime);
    
    if (onChange) {
      onChange(newTime);
    }
    setIsOpen(false);
  };

  const handleClear = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedTime(null);
    if (onChange) {
      onChange(null);
    }
    setIsOpen(false);
  };

  const formatDisplayTime = () => {
    if (!selectedTime || !selectedTime.isValid()) return '';
    
    if (hoursOnly) {
      return ampm ? selectedTime.format('h A') : selectedTime.format('HH:00');
    }
    if (ampm) {
      return selectedTime.format('h:mm A');
    }
    return selectedTime.format('HH:mm');
  };

  const generateHours = () => {
    const hours = [];
    const maxHour = ampm ? 12 : 23;
    const startHour = ampm ? 1 : 0;
    
    for (let i = startHour; i <= maxHour; i++) {
      hours.push(i);
    }
    
    return hours;
  };

  const generateMinutes = () => {
    const minutes = [];
    for (let i = 0; i < 60; i += minuteStep) {
      minutes.push(i);
    }
    return minutes;
  };

  const renderTimePicker = () => {
    const hours = generateHours();
    const minutes = generateMinutes();
    const popupTitle = hoursOnly ? 'Select Hour' : 'Select Time';

    return (
      <div 
        className={`mui-time-picker-popup ${isDarkMode ? 'dark' : 'light'}`}
        style={{ 
          '--theme-color': themeColor,
          backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
          border: `1px solid ${isDarkMode ? '#404040' : '#dee2e6'}`,
          borderRadius: '12px',
          padding: '16px',
          minWidth: '300px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ 
          marginBottom: '16px',
          textAlign: 'center'
        }}>
          <h6 style={{ 
            margin: 0, 
            fontSize: '16px', 
            fontWeight: '600',
            color: isDarkMode ? '#ffffff' : '#000000'
          }}>
            {popupTitle}
          </h6>
        </div>

        {/* Time Selection */}
        <div style={{ 
          display: 'flex',
          gap: '16px',
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          {/* Hours */}
          <div style={{ textAlign: 'center' }}>
            <label style={{ 
              display: 'block',
              marginBottom: '8px',
              fontSize: '12px',
              fontWeight: '600',
              color: isDarkMode ? '#adb5bd' : '#6c757d'
            }}>
              Hour
            </label>
            <select
              value={currentHour}
              onChange={(e) => setCurrentHour(parseInt(e.target.value))}
              style={{
                width: '70px',
                height: '40px',
                border: `1px solid ${isDarkMode ? '#404040' : '#dee2e6'}`,
                borderRadius: '8px',
                backgroundColor: isDarkMode ? '#1a1a1a' : '#ffffff',
                color: isDarkMode ? '#ffffff' : '#000000',
                fontSize: '14px',
                textAlign: 'center',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              {hours.map(hour => (
                <option key={hour} value={hour}>
                  {hour.toString().padStart(2, '0')}
                </option>
              ))}
            </select>
          </div>

          {!hoursOnly && (
            <>
              {/* Separator */}
              <div style={{ 
                fontSize: '24px', 
                fontWeight: 'bold',
                color: isDarkMode ? '#ffffff' : '#000000',
                marginTop: '20px'
              }}>
                :
              </div>

              {/* Minutes */}
              <div style={{ textAlign: 'center' }}>
                <label style={{ 
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: isDarkMode ? '#adb5bd' : '#6c757d'
                }}>
                  Minute
                </label>
                <select
                  value={currentMinute}
                  onChange={(e) => setCurrentMinute(parseInt(e.target.value))}
                  style={{
                    width: '70px',
                    height: '40px',
                    border: `1px solid ${isDarkMode ? '#404040' : '#dee2e6'}`,
                    borderRadius: '8px',
                    backgroundColor: isDarkMode ? '#1a1a1a' : '#ffffff',
                    color: isDarkMode ? '#ffffff' : '#000000',
                    fontSize: '14px',
                    textAlign: 'center',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  {minutes.map(minute => (
                    <option key={minute} value={minute}>
                      {minute.toString().padStart(2, '0')}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {/* AM/PM */}
          {ampm && (
            <div style={{ textAlign: 'center' }}>
              <label style={{ 
                display: 'block',
                marginBottom: '8px',
                fontSize: '12px',
                fontWeight: '600',
                color: isDarkMode ? '#adb5bd' : '#6c757d'
              }}>
                Period
              </label>
              <select
                value={isPM ? 'PM' : 'AM'}
                onChange={(e) => setIsPM(e.target.value === 'PM')}
                style={{
                  width: '70px',
                  height: '40px',
                  border: `1px solid ${isDarkMode ? '#404040' : '#dee2e6'}`,
                  borderRadius: '8px',
                  backgroundColor: isDarkMode ? '#1a1a1a' : '#ffffff',
                  color: isDarkMode ? '#ffffff' : '#000000',
                  fontSize: '14px',
                  textAlign: 'center',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </select>
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: '8px',
          paddingTop: '12px',
          borderTop: `1px solid ${isDarkMode ? '#404040' : '#dee2e6'}`
        }}>
          <button 
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(false);
            }}
            onMouseEnter={() => setIsCancelHovered(true)}
            onMouseLeave={() => setIsCancelHovered(false)}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '6px',
              background: isCancelHovered 
                ? (isDarkMode ? 'rgba(173, 181, 189, 0.1)' : 'rgba(108, 117, 125, 0.1)')
                : 'transparent',
              color: isDarkMode ? '#adb5bd' : '#6c757d',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'background 0.2s ease'
            }}
          >
            Cancel
          </button>
          <button 
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleTimeSelect();
            }}
            onMouseEnter={() => setIsOkHovered(true)}
            onMouseLeave={() => setIsOkHovered(false)}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: isOkHovered ? getDarkerThemeColor() : themeColor,
              color: isDarkMode ? '#ffffff' : '#000000',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'background-color 0.2s ease, color 0.2s ease',
              opacity: 1
            }}
          >
            OK
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className={`mui-time-picker-container ${isDarkMode ? 'dark' : 'light'} ${className}`}
      style={{ '--theme-color': themeColor, position: 'relative', ...style }}
    >
      {label && (
        <label 
          className={`mui-label ${error ? 'error' : ''}`}
          htmlFor={`time-input-${name}`}
          style={{ 
            display: 'block',
            marginBottom: '8px',
            fontSize: '14px',
            fontWeight: '500',
            color: isDarkMode ? '#ffffff' : '#333333'
          }}
        >
          {label}
        </label>
      )}
      
      <div style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        borderRadius: '10px',
        border: `1px solid ${isDarkMode ? '#404040' : '#D9D9D9'}`,
        backgroundColor: isDarkMode ? '#2d3436' : '#F9F9F9',
        height: '44px',
        transition: 'all 0.2s ease'
      }}>
        <input
          id={`time-input-${name}`}
          type="text"
          value={formatDisplayTime()}
          placeholder={placeholder}
          onClick={toggleTimePicker}
          readOnly
          disabled={disabled}
          name={name}
          style={{
            flex: 1,
            padding: '8px 12px',
            border: 'none',
            outline: 'none',
            background: 'transparent',
            fontSize: '14px',
            color: isDarkMode ? '#ffffff' : '#000000',
            cursor: 'pointer'
          }}
          {...props}
        />
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', paddingRight: '8px' }}>
          {/* {clearable && selectedTime && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                border: 'none',
                borderRadius: '6px',
                background: 'transparent',
                color: isDarkMode ? '#adb5bd' : '#6c757d',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              <i className="fa fa-times"></i>
            </button>
          )} */}
          <button
            type="button"
            onClick={toggleTimePicker}
            disabled={disabled}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px',
              border: 'none',
              borderRadius: '6px',
              background: 'transparent',
              color: isDarkMode ? '#adb5bd' : '#6c757d',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            <i className="fa fa-clock"></i>
          </button>
        </div>
      </div>

      {helperText && (
        <div style={{
          marginTop: '6px',
          fontSize: '12px',
          color: error ? '#d32f2f' : (isDarkMode ? '#adb5bd' : '#6c757d')
        }}>
          {helperText}
        </div>
      )}

      {isOpen && createPortal(
        <div 
          ref={popupRef}
          style={{
            position: 'fixed',
            top: `${popupPosition.top}px`,
            left: `${popupPosition.left}px`,
            zIndex: 10000,
            width: containerRef.current ? `${containerRef.current.offsetWidth}px` : '300px',
            minWidth: '300px'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {renderTimePicker()}
        </div>,
        document.body
      )}
    </div>
  );
};

export default MUITimePicker;